import { Database, Search, SearchX, TriangleAlert, Trash2 } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, type Session } from "./api";
import { demoCacheEntries } from "./demo";
import type { CacheEntry } from "./types";
import {
  DensityToggle,
  Empty,
  Field,
  fmtDate,
  fmtInt,
  Notice,
  PageHead,
  useIsNarrow,
  useSearchShortcut,
  type Density
} from "./ui";

type CacheStats = {
  total_entries: number;
  entries_by_source: Record<string, number>;
  paid_source_entries: number;
  free_source_entries: number;
  total_hits: number;
  newest_entry_at: string | null;
};

export function CacheView({
  session,
  demo,
  density,
  onDensityChange
}: {
  session: Session;
  demo: boolean;
  density: Density;
  onDensityChange: (next: Density) => void;
}) {
  const [stats, setStats] = useState<CacheStats>();
  const [entries, setEntries] = useState<CacheEntry[]>();
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();
  const titleRef = useRef<HTMLInputElement>(null);
  const narrow = useIsNarrow();
  const canInvalidate = session.permissions.includes("cache.invalidate");

  useSearchShortcut(titleRef);

  useEffect(() => {
    if (demo) {
      setStats({
        total_entries: 13_727_518,
        entries_by_source: { crossref: 8_102_141, pubmed: 3_844_002, openalex: 1_781_375 },
        paid_source_entries: 92_140,
        free_source_entries: 13_635_378,
        total_hits: 0,
        newest_entry_at: "2026-08-13T18:20:00Z"
      });
      return;
    }
    void api<CacheStats>("/api/cache/stats", session)
      .then(setStats)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "Cache statistics could not be loaded")
      );
  }, [demo, session]);

  async function runSearch(event: FormEvent) {
    event.preventDefault();
    setError(undefined);
    setMessage(undefined);
    setSelected([]);
    setReviewing(false);
    try {
      const data = demo
        ? { entries: demoCacheEntries }
        : await api<{ entries: CacheEntry[] }>(
            `/api/cache/search?${new URLSearchParams({
              ...(title ? { title } : {}),
              ...(author ? { author } : {})
            })}`,
            session
          );
      setEntries(data.entries);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The cache search failed");
    }
  }

  async function invalidate(event: FormEvent) {
    event.preventDefault();
    if (!reviewing) {
      setReviewing(true);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      if (!demo)
        await api("/api/cache/invalidate", session, {
          method: "POST",
          body: JSON.stringify({ ids: selected, reason: reason.trim() })
        });
      const count = selected.length;
      setEntries((current) => current?.filter((entry) => !selected.includes(entry.id)));
      setMessage(
        `${count} cache ${count === 1 ? "entry was" : "entries were"} invalidated. The next request for ${count === 1 ? "it" : "them"} will resolve fresh.`
      );
      setSelected([]);
      setReason("");
      setReviewing(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The entries could not be invalidated");
      setReviewing(false);
    } finally {
      setBusy(false);
    }
  }

  const rows = entries ?? [];
  const allSelected = rows.length > 0 && selected.length === rows.length;
  const reasonOk = reason.trim().length >= 10;

  return (
    <section className="view">
      <PageHead
        title="Legacy cache"
        count={entries ? `${fmtInt(rows.length)} found` : undefined}
        authority="GenieCore v5"
      >
        <DensityToggle density={density} onChange={onDensityChange} />
      </PageHead>

      <dl className="readouts">
        <div className="readout">
          <dt>Entries held</dt>
          <dd className="num">{stats ? fmtInt(stats.total_entries) : "—"}</dd>
        </div>
        <div className="readout">
          <dt>Free-source records</dt>
          <dd className="num">{stats ? fmtInt(stats.free_source_entries) : "—"}</dd>
        </div>
        <div className="readout">
          <dt>Paid-source records</dt>
          <dd className="num">{stats ? fmtInt(stats.paid_source_entries) : "—"}</dd>
          <span className="sub">Costly to re-resolve</span>
        </div>
        <div className="readout">
          <dt>Newest fetch</dt>
          <dd className="num" style={{ fontSize: 18 }}>
            {stats?.newest_entry_at ? fmtDate(stats.newest_entry_at) : "—"}
          </dd>
        </div>
      </dl>

      <form className="toolbar" onSubmit={(event) => void runSearch(event)}>
        <div className="search" style={{ flex: "1 1 220px" }}>
          <Search aria-hidden="true" />
          <input
            ref={titleRef}
            type="search"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            aria-label="Title contains"
            placeholder="Title contains"
          />
          <kbd aria-hidden="true">/</kbd>
        </div>
        <div className="search" style={{ flex: "0 1 200px" }}>
          <input
            type="search"
            value={author}
            onChange={(event) => setAuthor(event.target.value)}
            aria-label="Author surname"
            placeholder="Author surname"
          />
        </div>
        <button type="submit" className="btn btn-default" disabled={!title && !author}>
          Search cache
        </button>
        <span className="toolbar-note">Search only — there is no table browser</span>
      </form>

      {error && (
        <Notice tone="error" className="toolbar-notice">
          {error}
        </Notice>
      )}
      {message && (
        <Notice tone="success" className="toolbar-notice">
          {message}
        </Notice>
      )}

      <div className="table-wrap">
        <table className="records fixed">
          <colgroup>
            <col style={{ width: "38px" }} />
            {narrow ? (
              <>
                <col style={{ width: "62%" }} />
                <col style={{ width: "32%" }} />
              </>
            ) : (
              <>
                <col style={{ width: "34%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "16%" }} />
              </>
            )}
          </colgroup>
          <thead>
            <tr>
              <th scope="col" className="check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={!rows.length || !canInvalidate}
                  aria-label="Select every result"
                  onChange={(event) =>
                    setSelected(event.target.checked ? rows.map((entry) => entry.id) : [])
                  }
                />
              </th>
              <th scope="col">Work</th>
              {!narrow && <th scope="col">Identifier</th>}
              {!narrow && <th scope="col">Source</th>}
              {!narrow && (
                <th scope="col" className="right">
                  Hits
                </th>
              )}
              <th scope="col" className="right">
                Fetched
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => {
              const checked = selected.includes(entry.id);
              return (
                <tr
                  key={entry.id}
                  data-checked={checked ? "true" : undefined}
                  onClick={() => {
                    if (!canInvalidate) return;
                    setReviewing(false);
                    setSelected((current) =>
                      current.includes(entry.id)
                        ? current.filter((id) => id !== entry.id)
                        : [...current, entry.id]
                    );
                  }}
                >
                  <td className="check">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!canInvalidate}
                      aria-label={`Select ${entry.title_preview ?? entry.canonical_id ?? entry.id}`}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        setReviewing(false);
                        setSelected((current) =>
                          event.target.checked
                            ? [...current, entry.id]
                            : current.filter((id) => id !== entry.id)
                        );
                      }}
                    />
                  </td>
                  <td className="cell-primary">
                    <span className="cell-stack">
                      {entry.title_preview ?? entry.canonical_id ?? "Untitled cache record"}
                      <small>{entry.authors_preview ?? "Author unknown"}</small>
                    </span>
                  </td>
                  {!narrow && <td className="cell-muted mono">{entry.canonical_id ?? "—"}</td>}
                  {!narrow && (
                    <td className="cell-muted">
                      {entry.source_engine}
                      {entry.is_paid_source && <span className="tag">Paid</span>}
                    </td>
                  )}
                  {!narrow && <td className="right num cell-muted">{fmtInt(entry.hit_count)}</td>}
                  <td className="right cell-faint">{fmtDate(entry.fetched_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {!entries && (
          <Empty icon={Search} title="Search the cache by title or author">
            The console deliberately offers no table browser or arbitrary query box. Start from a
            work you already know is wrong.
          </Empty>
        )}
        {entries && !rows.length && (
          <Empty icon={SearchX} title="No cached record matches">
            Try a shorter title fragment, or search by the author's surname instead.
          </Empty>
        )}
      </div>

      {reviewing && selected.length > 0 && (
        <p className="review-strip" role="alert">
          <TriangleAlert aria-hidden="true" />
          <span>
            This permanently deletes {selected.length}{" "}
            {selected.length === 1 ? "cached record" : "cached records"}. Any later request for{" "}
            {selected.length === 1 ? "it" : "them"} must be resolved again, and paid sources are
            charged a second time.
          </span>
        </p>
      )}

      {canInvalidate && selected.length > 0 && (
        <form className="action-bar" onSubmit={(event) => void invalidate(event)}>
          <span className="selected-count">
            <Database aria-hidden="true" width={15} height={15} />
            <b>{selected.length}</b> selected
          </span>
          <Field
            label="Reason"
            htmlFor="cache-reason"
            hint={reasonOk ? undefined : "10 characters minimum"}
          >
            <input
              id="cache-reason"
              type="text"
              required
              minLength={10}
              maxLength={500}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setReviewing(false);
              }}
              placeholder="What is wrong with these cached records?"
            />
          </Field>
          <button
            type="button"
            className="btn btn-quiet"
            onClick={() => {
              setSelected([]);
              setReviewing(false);
            }}
          >
            Clear
          </button>
          <button type="submit" className="btn btn-danger" disabled={!reasonOk || busy}>
            <Trash2 aria-hidden="true" />
            {busy ? "Invalidating…" : reviewing ? "Confirm invalidation" : "Invalidate…"}
          </button>
        </form>
      )}
    </section>
  );
}
